import type { InvoiceDate } from '@vy/types';
import { MONTHS_LABELS } from '../../../customers/OldCustomerOverviewPage/customer-view-page-components/BillingScheduleChart/billing-schedule-chart-consts';
import { isEmpty, isSingleItem } from '../../../../../../../libs/array/src/services';

export function formatYearShort(year: number): string {
  return year.toString().slice(-2);
}

function getMonthName(month: string): string {
  // If month is already a string from MONTHS_LABELS, return it
  if (MONTHS_LABELS.includes(month)) {
    return month;
  }
  // Otherwise, treat it as an index
  const index = parseInt(month, 10);
  return MONTHS_LABELS[index] || '';
}

function getDatesForYear(dates: InvoiceDate[], year: number): InvoiceDate[] {
  return dates
    .filter((date) => date.year === year)
    .sort((a, b) => {
      const monthA = getMonthName(a.month);
      const monthB = getMonthName(b.month);
      return MONTHS_LABELS.indexOf(monthA) - MONTHS_LABELS.indexOf(monthB);
    });
}

export function isContinuousMonthRange(monthIndices: number[]): boolean {
  if (isEmpty(monthIndices)) {
    return false;
  }

  const minIndex = monthIndices[0];
  const maxIndex = monthIndices[monthIndices.length - 1];
  const expectedCount = maxIndex - minIndex + 1;

  return monthIndices.length === expectedCount;
}

function formatSelectedDatesDisplayInternal(
  dates: InvoiceDate[],
  year: number,
  multipleFormat: 'count' | 'list',
): string {
  const datesForYear = getDatesForYear(dates, year);

  if (isEmpty(datesForYear)) {
    return '';
  }

  if (isSingleItem(datesForYear)) {
    const monthName = getMonthName(datesForYear[0].month);
    return `${monthName} ${formatYearShort(year)}`;
  }

  const monthIndices = datesForYear.map((date) => {
    const monthName = getMonthName(date.month);
    return MONTHS_LABELS.indexOf(monthName);
  });

  if (isContinuousMonthRange(monthIndices)) {
    const startMonth = MONTHS_LABELS[monthIndices[0]];
    const endMonth = MONTHS_LABELS[monthIndices[monthIndices.length - 1]];
    return `${startMonth}-${endMonth} ${formatYearShort(year)}`;
  }

  // Multiple non-continuous months
  if (multipleFormat === 'count') {
    return `Multiple (${datesForYear.length})`;
  }

  const yearShort = formatYearShort(year);
  return datesForYear.map((date) => {
    const monthName = getMonthName(date.month);
    return `${monthName} ${yearShort}`;
  }).join(', ');
}

export function formatSelectedDatesDisplay(dates: InvoiceDate[], year: number): string {
  return formatSelectedDatesDisplayInternal(dates, year, 'list');
}

export function formatSelectedDatesTooltip(dates: InvoiceDate[], year: number): string {
  const datesForYear = getDatesForYear(dates, year);

  if (isEmpty(datesForYear)) {
    return '';
  }

  const yearShort = formatYearShort(year);
  return datesForYear.map((date) => {
    const monthName = getMonthName(date.month);
    return `${monthName} ${yearShort}`;
  }).join(', ');
}

export function formatDatesDisplay(dates: InvoiceDate[]): string {
  if (!dates || isEmpty(dates)) {
    return '';
  }

  // Group dates by year
  const years = Array.from(new Set(dates.map((date) => date.year))).sort();

  if (isSingleItem(years)) {
    return formatSelectedDatesDisplay(dates, years[0]);
  }

  // Multiple years
  const yearDisplays = years.map((year) => {
    const yearDates = getDatesForYear(dates, year);
    return formatSelectedDatesDisplay(yearDates, year);
  });

  return yearDisplays.join(', ');
}

export function formatSelectedDatesDisplayWithMonthString(dates: InvoiceDate[]): string {
  if (isEmpty(dates)) {
    return '';
  }

  const years = Array.from(new Set(dates.map((date) => date.year))).sort();

  if (isSingleItem(years)) {
    return formatSelectedDatesDisplayInternal(dates, years[0], 'count');
  }

  return `Multiple (${dates.length})`;
}

export function formatSelectedDatesTooltipWithMonthString(dates: InvoiceDate[]): string {
  if (isEmpty(dates)) {
    return '';
  }

  const sortedDates = [...dates].sort((a, b) => {
    const yearDiff = a.year - b.year;
    if (yearDiff !== 0) {
      return yearDiff;
    }
    const monthA = getMonthName(a.month);
    const monthB = getMonthName(b.month);
    return MONTHS_LABELS.indexOf(monthA) - MONTHS_LABELS.indexOf(monthB);
  });

  return sortedDates
    .map((date) => {
      const monthName = getMonthName(date.month);
      return `${monthName} ${formatYearShort(date.year)}`;
    })
    .join(', ');
}